// Service for grouping pending anime in discover section by user's existing groups

import { getDisplayTitle } from '../hooks/useDisplayTitle';
import databaseService from './sqliteService';

/**
 * Group pending anime by which user group they are related to
 * @param {Array} pendingAnime - Array of pending anime from detectPendingAnime
 * @param {Array} userGroups - User's existing anime groups
 * @returns {Array} Array of grouped pending anime with metadata
 */
export const groupPendingAnimeByRelation = (pendingAnime, userGroups) => {
    if (!pendingAnime || pendingAnime.length === 0) {
        return [];
    }

    if (!userGroups || userGroups.length === 0) {
        // If no user groups exist, put everything in "Ungrouped"
        return [{
            id: 'ungrouped',
            name: 'Ungrouped',
            pendingAnime: pendingAnime,
            stats: calculatePendingGroupStats(pendingAnime),
            isUngrouped: true
        }];
    }

    // Map to track which group each pending anime belongs to
    const groupMap = new Map();
    const ungroupedAnime = [];
    const droppedRelatedAnime = []; // Track anime to store in dropped-related DB

    // For each pending anime, find which group it's related to
    pendingAnime.forEach(anime => {
        // PRIMARY: Match by server cluster group ID (handles indirect relations)
        if (anime.clusterGroupId) {
            const clusterBasedId = `cluster-${anime.clusterGroupId}`;
            const matchingGroup = userGroups.find(group => group.id === clusterBasedId);
            
            if (matchingGroup) {
                // Check if this group has ONLY dropped anime - if so, skip it
                const nonDroppedAnime = matchingGroup.anime.filter(entry => entry.status !== 'DROPPED');
                if (nonDroppedAnime.length === 0) {
                    droppedRelatedAnime.push(anime.id);
                    return;
                }

                const groupId = matchingGroup.id;
                if (!groupMap.has(groupId)) {
                    groupMap.set(groupId, {
                        id: groupId,
                        name: matchingGroup.name,
                        pendingAnime: [],
                        userGroup: matchingGroup
                    });
                }
                groupMap.get(groupId).pendingAnime.push(anime);
                return;
            }
        }

        // FALLBACK: Match by relatedTo.id (for cached data without clusterGroupId)
        const relatedToId = anime.relatedTo?.id;

        if (!relatedToId) {
            ungroupedAnime.push(anime);
            return;
        }

        // Find which group contains the anime this pending anime is related to
        const matchingGroup = userGroups.find(group =>
            group.anime.some(entry => entry.media.id === relatedToId)
        );

        if (matchingGroup) {
            // Check if this group has ONLY dropped anime - if so, skip it and store in dropped-related DB
            const nonDroppedAnime = matchingGroup.anime.filter(entry => entry.status !== 'DROPPED');
            if (nonDroppedAnime.length === 0) {
                // All anime in this group are dropped, store this anime in dropped-related DB
                droppedRelatedAnime.push(anime.id);
                return;
            }

            const groupId = matchingGroup.id;
            if (!groupMap.has(groupId)) {
                groupMap.set(groupId, {
                    id: groupId,
                    name: matchingGroup.name,
                    pendingAnime: [],
                    userGroup: matchingGroup
                });
            }
            groupMap.get(groupId).pendingAnime.push(anime);
        } else {
            ungroupedAnime.push(anime);
        }
    });

    // Save dropped-related anime to database AND server (async, fire-and-forget)
    if (droppedRelatedAnime.length > 0) {
        databaseService.batchAddDroppedRelatedAnime(droppedRelatedAnime).then(() => {
            console.log(`📦 Stored ${droppedRelatedAnime.length} dropped-related anime in local DB`);
        }).catch(err => {
            console.error('Error storing dropped-related anime locally:', err);
        });

        // Also sync to server (fire-and-forget)
        const { hasToken } = require('./tokenStorage');
        if (hasToken()) {
            import('./apiService').then(({ batchAddDroppedRelatedOnServer }) => {
                batchAddDroppedRelatedOnServer(droppedRelatedAnime).then(() => {
                    console.log(`📡 Synced ${droppedRelatedAnime.length} dropped-related anime to server`);
                }).catch(err => {
                    console.warn('⚠️ Failed to sync dropped-related anime to server:', err.message);
                });
            });
        }
    }

    // Convert map to array and calculate stats
    const groups = Array.from(groupMap.values()).map(group => ({
        ...group,
        stats: calculatePendingGroupStats(group.pendingAnime)
    }));

    // Add ungrouped if any exist
    if (ungroupedAnime.length > 0) {
        groups.push({
            id: 'ungrouped',
            name: 'Ungrouped',
            pendingAnime: ungroupedAnime,
            stats: calculatePendingGroupStats(ungroupedAnime),
            isUngrouped: true
        });
    }

    return groups;
};

/**
 * Calculate statistics for a group of pending anime
 * @param {Array} pendingAnime - Array of pending anime
 * @returns {Object} Statistics object
 */
export const calculatePendingGroupStats = (pendingAnime) => {
    if (!pendingAnime || pendingAnime.length === 0) {
        return {
            count: 0,
            averageScore: 0,
            totalEpisodes: 0,
            relationTypes: {}
        };
    }

    let totalScore = 0;
    let scoredCount = 0;
    let totalEpisodes = 0;
    const relationTypes = {};

    pendingAnime.forEach(anime => {
        // Average score
        if (anime.averageScore && anime.averageScore > 0) {
            totalScore += anime.averageScore;
            scoredCount++;
        }

        // Total episodes
        if (anime.episodes) {
            totalEpisodes += anime.episodes;
        }

        // Relation type breakdown
        const relationType = anime.relationType || 'UNKNOWN';
        relationTypes[relationType] = (relationTypes[relationType] || 0) + 1;
    });

    return {
        count: pendingAnime.length,
        averageScore: scoredCount > 0 ? Math.round(totalScore / scoredCount) : 0,
        totalEpisodes,
        relationTypes
    };
};

/**
 * Sort pending groups by various criteria
 * @param {Array} groups - Array of pending groups
 * @param {string} sortBy - Sort criteria: 'name', 'count', 'score'
 * @param {string} direction - 'asc' or 'desc'
 * @returns {Array} Sorted groups
 */
export const sortPendingGroups = (groups, sortBy = 'name', direction = 'asc') => {
    if (!groups || groups.length === 0) {
        return [];
    }

    const dir = direction === 'asc' ? 1 : -1;

    return [...groups].sort((a, b) => {
        // Always put "Ungrouped" at the end
        if (a.isUngrouped) return 1;
        if (b.isUngrouped) return -1;

        switch (sortBy) {
            case 'name':
                return dir * a.name.localeCompare(b.name);
            case 'count':
                return dir * (b.stats.count - a.stats.count);
            case 'score':
                return dir * (b.stats.averageScore - a.stats.averageScore);
            default:
                return 0;
        }
    });
};

/**
 * Filter pending groups by search query
 * @param {Array} groups - Array of pending groups
 * @param {string} query - Search query
 * @returns {Array} Filtered groups
 */
export const filterPendingGroups = (groups, query, titlePreference = null) => {
    if (!query || !query.trim()) {
        return groups;
    }

    const lowerQuery = query.toLowerCase().trim();

    return groups
        .map(group => {
            // Filter anime within group
            const filteredAnime = group.pendingAnime.filter(anime =>
                getDisplayTitle(anime, titlePreference).toLowerCase().includes(lowerQuery) ||
                anime.relatedTo?.title.toLowerCase().includes(lowerQuery)
            );

            // Include group if name matches or has matching anime
            if (group.name.toLowerCase().includes(lowerQuery) || filteredAnime.length > 0) {
                return {
                    ...group,
                    pendingAnime: filteredAnime.length > 0 ? filteredAnime : group.pendingAnime,
                    stats: filteredAnime.length > 0 ? calculatePendingGroupStats(filteredAnime) : group.stats
                };
            }

            return null;
        })
        .filter(Boolean);
};
